import {
  Chip,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { FC, useEffect, useState } from 'react'
import dayjs from 'dayjs'

import { useFetchAccountsPayablesLazyQuery } from '../../../../generated'
import { FILTER_DATE_FORMAT, GRAPHQL_QUERY_POLICY, NOT_APPLICABLE } from '../../../constants'
import { enumToLabel, getProperPaymentMethod } from '../../../utils'
import { payableStatusColors } from '../../../utils/billing'
import { CustomModal } from '../../common/CustomModal'
import { CustomTableCell } from '../../common/CustomTableCell'
import { StickyTable } from '../../common/StickyTable'
import { PaginationWithJump } from '../../common/PaginationWithJump'
import { TableSkeleton } from '../../skeletons/TableSkeleton'
import { EmptyData } from '../../common/EmptyData'

interface PayableLookupModalProps {
  isOpen: boolean
  onClose: () => void
  keyword: string
  vendorId?: number
}

const LOOKUP_HEADERS = ['Voucher #', 'Vendor', 'Matter', 'Notes / Invoice No.', 'Pay Date', 'Payment Method', 'Status', 'Amount']
const LOOKUP_LIMIT = 20

const PayableLookupModal: FC<PayableLookupModalProps> = ({ isOpen, onClose, keyword, vendorId }) => {
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const [fetchPayables, { data, loading }] = useFetchAccountsPayablesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined
  })

  const payables = data?.fetchAccountsPayables?.apEntries ?? []

  const buildInput = (pageNo: number) => ({
    descriptionKeyword: keyword,
    vendorId: vendorId || undefined,
    paginationOptions: { page: pageNo, limit: LOOKUP_LIMIT }
  })

  useEffect(() => {
    if (isOpen && keyword) {
      setPage(1)
      fetchPayables({ variables: { input: buildInput(1) } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, keyword, vendorId])

  useEffect(() => {
    if (data?.fetchAccountsPayables?.pagination) {
      setTotalPages(data.fetchAccountsPayables.pagination.totalPages || 1)
    }
  }, [data])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    fetchPayables({ variables: { input: buildInput(newPage) } })
  }

  const handleRowClick = (voucherNo: string) => {
    window.open(`/accounts/payable/${voucherNo}`, '_blank')
  }

  return (
    <CustomModal isOpen={isOpen} handleClose={onClose} title="Payable Lookup" maxWidth="lg">
      <Typography variant="body2" color="text.secondary" mb={2}>
        Showing results for &quot;{keyword}&quot;{vendorId ? ' (filtered by selected vendor)' : ''}
      </Typography>

      {!loading && payables.length === 0 ? (
        <EmptyData title="No matching payables found" iconSize="80px" />
      ) : (
        <StickyTable
          height="400px"
          pagination={
            totalPages > 1 ? (
              <PaginationWithJump
                disabled={loading}
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                sx={{ pt: 2, pb: 2 }}
              />
            ) : undefined
          }
        >
          <TableHead>
            <TableRow>
              {LOOKUP_HEADERS.map((header, index) => (
                <TableCell key={index} component="th" align="left" sx={{ whiteSpace: 'nowrap' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {loading ? (
            <TableSkeleton noOfRows={5} noOfCols={LOOKUP_HEADERS.length} />
          ) : (
            <TableBody>
              {payables.map(payable => {
                const matter = payable.lineItems?.find(item => item.litigation)?.litigation

                return (
                  <TableRow
                    key={payable.voucherNo}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(payable.voucherNo)}
                  >
                    <CustomTableCell value={payable.voucherNo} />
                    <CustomTableCell value={payable.vendor ? `(${payable.vendor.vendorId}) ${payable.vendor.name}` : NOT_APPLICABLE} />
                    <CustomTableCell
                      value={matter ? `#${matter?.caseNumber ?? matter?.consultMatterNumber ?? ''}` : NOT_APPLICABLE}
                    />
                    <TableCell>
                      <Typography variant="body2">
                        {payable.description || NOT_APPLICABLE}
                      </Typography>
                    </TableCell>
                    <CustomTableCell value={dayjs(payable.payDate).format(FILTER_DATE_FORMAT)} />
                    <CustomTableCell value={getProperPaymentMethod(payable.paymentMethod)} />
                    <TableCell>
                      <Chip
                        label={enumToLabel(payable.status)}
                        size="small"
                        sx={{
                          color: payableStatusColors[payable.status],
                          border: `1px solid ${payableStatusColors[payable.status]}50`,
                          backgroundColor: 'transparent'
                        }}
                      />
                    </TableCell>
                    <CustomTableCell value={payable.amount.toFixed(2)} />
                  </TableRow>
                )
              })}
            </TableBody>
          )}
        </StickyTable>
      )}
    </CustomModal>
  )
}

export default PayableLookupModal
